"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, HelpCircle, MessageCircle } from "lucide-react";
import { whatsappHref } from "@/lib/support-channel";

type FAQ = {
    question: string;
    answer: string;
    /** A-26 answer items (PRICE_1000, LEGIT) carry their kind; G-PAA counts them in the served HTML. */
    kind?: "price1000" | "legit";
    /** One link rendered after the answer (PRICE_1000 → the locale's price guide). */
    link?: { href: string; label: string };
};

type Props = {
    faqs: FAQ[];
    platform: string;
    service: string;
    /**
     * "service" (R1, the commerce routes): the FAQ heading and the questions only. The shared subtitle ("Everything
     * you need to know about {platform} {service}") and the support band were the same on all 16 service pages of a
     * locale; the support channel now sits once in the trust block above (A-16, P-2).
     */
    variant?: "default" | "service";
};

export default function ServiceFAQ({ faqs, platform, service, variant = "default" }: Props) {
    // Was entirely hardcoded English — it did not even receive `locale`, so a Turkish
    // page showed "Frequently Asked Questions" above its Turkish questions.
    const t = useTranslations("serviceUi");
    const tSupport = useTranslations("supportChannel");
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    return (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white" data-service-faq="true">
            <div className="container-custom">
                <div className="max-w-4xl mx-auto">
                    {/* Section Header */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-4">
                            <HelpCircle className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-700">FAQ</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            {t("faqTitle")}
                        </h2>
                        {variant !== "service" && (
                            <p className="text-lg text-gray-600">
                                {t("faqSubtitle", { platform, service })}
                            </p>
                        )}
                    </div>

                    {/* FAQ Accordion */}
                    <div className="space-y-4">
                        {faqs.map((faq, index) => {
                            const isOpen = openIndex === index;
                            return (
                                <div
                                    key={index}
                                    data-faq-item="true"
                                    data-faq-kind={faq.kind}
                                    className={`bg-white rounded-2xl border transition-all duration-300 ${isOpen
                                            ? "border-emerald-200 shadow-lg shadow-emerald-100/50"
                                            : "border-gray-100 hover:border-gray-200"
                                        }`}
                                >
                                    <button
                                        onClick={() => setOpenIndex(isOpen ? null : index)}
                                        aria-expanded={isOpen}
                                        className="w-full flex items-center justify-between p-6 text-left"
                                    >
                                        <span className={`font-semibold text-lg pr-4 ${isOpen ? "text-emerald-800" : "text-gray-900"}`}>
                                            {faq.question}
                                        </span>
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${isOpen
                                                ? "bg-emerald-500 text-white rotate-180"
                                                : "bg-gray-100 text-gray-500"
                                            }`}>
                                            <ChevronDown className="w-5 h-5" />
                                        </div>
                                    </button>

                                    {/* Answer. Was `max-h-96` when open: on a 390px phone 65 of 519
                                        authored answers were cut off mid-sentence while the FAQPage
                                        JSON-LD still declared the full text. The 0fr→1fr grid-row
                                        transition animates to the content's own height, any length. */}
                                    <div aria-hidden={!isOpen} className={`grid transition-[grid-template-rows] duration-300 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                                        <div className="overflow-hidden">
                                            <div className="px-6 pb-6">
                                                <div className="pt-4 border-t border-gray-100 text-gray-600 leading-relaxed">
                                                    {faq.answer}
                                                    {faq.link && (
                                                        <>
                                                            {" "}
                                                            <a href={faq.link.href} className="font-semibold text-emerald-700 underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600">
                                                                {faq.link.label} →
                                                            </a>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {variant !== "service" && (<>
                    {/* Contact CTA. G05 acceptance B1 (2026-10-03): the band was emerald-500 -> teal-500,
                        2.42:1 under the white statement and 2.30:1 under the emerald-50 support line
                        (WCAG 1.4.3 needs 4.5:1; axe returns "incomplete" on gradients, so Lighthouse
                        cannot see it). The 700 stops give white 5.36:1 at both ends. Colour only.
                        Pinned by lib/__tests__/release-v130-contrast-pins.test.ts. */}
                    <div className="mt-12 bg-gradient-to-r from-emerald-700 to-teal-700 rounded-3xl p-8 text-center">
                        <h3 className="text-2xl font-bold text-white mb-2">{t("faqStillQuestions")}</h3>
                        {/* G05 (owner R04): the 24/7 WhatsApp statement, and a support line that no
                            longer contradicts it (tr/en/es/pt said "replies during working hours",
                            de/fr "round the clock"). No response-time promise. */}
                        <p className="font-semibold text-white mb-1" data-support-statement="true">{tSupport("statement")}</p>
                        <p className="text-white mb-6">{t("faqSupportLine")}</p>
                        {/* Was a <button> with no handler — a dead control on every service page,
                            then a link to the contact page. Now it opens the channel itself. */}
                        <a href={whatsappHref(tSupport("startMessage"))} target="_blank" rel="noopener noreferrer" data-faq-whatsapp="true" className="inline-flex min-h-11 items-center gap-2 px-6 py-3 bg-white text-emerald-800 font-semibold rounded-xl hover:shadow-lg transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
                            <MessageCircle className="w-5 h-5" aria-hidden="true" />
                            {tSupport("cta")}
                        </a>
                    </div>
                    </>)}
                </div>
            </div>
        </section>
    );
}
